import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ElementType, FC, ReactNode } from "react";
import { FONT_SIZE, MOTION } from "../../../theme/tokens";

/**
 * Semantic tone for a `SystemBanner`. Each maps onto the theme status palette — never a hardcoded
 * hex — so banners read as one family with `MetaChip` / `CandidateStatusChip`.
 */
export type SystemBannerTone = "info" | "success" | "warning" | "error";

type Props = {
	/** Already-translated headline. */
	title: ReactNode;
	/** Already-translated supporting copy. Omit for a one-line banner. */
	description?: ReactNode;
	/**
	 * Element the description renders as. Default `"p"`. Pass `"div"` when the copy is itself
	 * block-level markup (a `<ul>` of per-item detail) — a list inside a `<p>` is invalid nesting
	 * and browsers silently close the paragraph around it, which breaks the banner's layout.
	 */
	descriptionComponent?: ElementType;
	/** Leading icon; auto-tinted to the tone's ink. */
	icon?: ReactNode;
	/** Semantic tone — drives fill / border / ink from theme tokens. Default `info`. */
	tone?: SystemBannerTone;
	/** Trailing slot for an action (e.g. a `SystemButton`) or a dismiss control. */
	action?: ReactNode;
	/** Layout overrides — the caller owns placement/width, not the banner's ink. */
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

// Mount fade: opacity + a small lift. Transform/opacity only; reduced motion drops it.
const ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes systemBannerEnter": {
		from: { opacity: 0, transform: "translateY(-4px)" },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `systemBannerEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/**
 * A tinted inline callout that explains something about the surface it sits on — a caveat, a
 * degraded data source, a "these numbers include an estimate" note.
 *
 * Generic over any page: it owns only the tone-to-token mapping and the icon/title/description/
 * action layout. Reach for this instead of a hand-rolled tinted `<Box>` + `<Typography>` pair, and
 * instead of MUI `<Alert>` (which brings its own severity palette rather than ours).
 *
 * For a transient outcome message use the snackbar; for "nothing here yet" use `EmptyState`.
 */
export const SystemBanner: FC<Props> = ({ title, description, descriptionComponent = "p", icon, tone = "info", action, sx, dataTestId }) => (
	<Box
		data-testid={dataTestId}
		role="note"
		sx={[
			ENTRANCE_SX,
			(theme) => ({
				display: "flex",
				alignItems: "flex-start",
				gap: 1.25,
				px: 1.75,
				py: 1.25,
				borderRadius: 2,
				border: `1px solid ${alpha(theme.palette[tone].main, 0.26)}`,
				backgroundColor: alpha(theme.palette[tone].main, 0.08),
				color: theme.palette[tone].dark,
				"& > svg": { fontSize: 18, flexShrink: 0, mt: 0.125 },
			}),
			...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
		]}
	>
		{icon}
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<Typography component="p" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, lineHeight: 1.35 }}>
				{title}
			</Typography>
			{description && (
				<Typography component={descriptionComponent} sx={{ fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.45, mt: 0.25, color: "inherit", opacity: 0.85 }}>
					{description}
				</Typography>
			)}
		</Box>
		{action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
	</Box>
);

export default SystemBanner;
